import { GerenciadorPaginasItens } from "@fl/shared/src/types/menus";
import { UpdatePaginasMenu } from "@fl/shared/src/types/models/suporte_fl/paginas_menus";
import {
  GrupoPermissaoAttributes,
  PermissaoAttributes,
} from "@fl/shared/src/types/models/suporte_fl/permissoes";
import { useEffect, useState } from "react";
import { Button, Container, Modal } from "react-bootstrap";
import {
  BiCheckbox,
  BiCheckSquare,
  BiCollapseVertical,
  BiExpandVertical,
} from "react-icons/bi";
import ApiPermissions from "../../../services/suporte/auth/permissions";
import {
  checkAllMenus,
  checkParentMenus,
  collapseAllMenus,
  onCheckMenus,
  onCollapseMenus,
  organizeMenus,
} from "../../../utils/menusFuncs";
import SortFunctions from "../../../utils/sortArrays";
import MenuToolManagerItem from "../../menus/manager/item";

interface IProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedGroup: GrupoPermissaoAttributes | null;
  baseMenus: UpdatePaginasMenu[];
  onUpdatePermissions: (
    id_grupo_permissao: number,
    updated_permissions: PermissaoAttributes[]
  ) => void;
}

const ModalPermissionsMenus: React.FC<IProps> = ({
  showModal,
  setShowModal,
  selectedGroup,
  baseMenus,
  onUpdatePermissions,
}) => {
  const [menus, setMenus] = useState<UpdatePaginasMenu[]>([]);
  const [pages, setPages] = useState<GerenciadorPaginasItens[]>([]);
  const [permissions, setPermissions] = useState<PermissaoAttributes[]>();
  const [hasChanges, setHasChanges] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [collapseAll, setCollapseAll] = useState(false);
  const [elementsMenus, setElementsMenus] = useState<JSX.Element[]>([]);

  useEffect(() => {
    if (showModal) {
      const loadPermissions = async () => {
        if (selectedGroup) {
          const result = await new ApiPermissions({
            id_grupo_permissao: selectedGroup.id_grupo_permissao,
          }).listPermissions();
          if (result.success) setPermissions(result.data);
        }
      };

      loadPermissions();
    }
  }, [selectedGroup, showModal]);

  useEffect(() => {
    if (permissions) {
      if (baseMenus.length > 0 && menus.length === 0) {
        if (permissions.length === 0) {
          const _menus = baseMenus.map((item) => {
            item.checked = false;
            return item;
          });

          setMenus([..._menus]);
        } else {
          const _menus = baseMenus.map((item) => {
            const findPermission = permissions.find(
              (p) => p.id_pagina_menu === item.id_pagina_menu
            );
            if (findPermission) {
              item.checked = true;
            } else {
              item.checked = false;
            }
            return item;
          });

          setMenus([..._menus]);
        }
      }
    }
  }, [baseMenus, permissions]);

  useEffect(() => {
    if (menus.length > 0 && pages.length === 0) {
      const initialPages: GerenciadorPaginasItens[] = [];
      const finalPages = [
        ...organizeMenus(
          menus,
          menus.filter((item) => item.id_pagina_menu_pai === null),
          initialPages,
          0
        ),
      ];
      setPages(finalPages);
    }
  }, [menus]);

  useEffect(() => {
    if (selectedGroup && menus.length > 0 && pages.length > 0) {
      const updated_menus = checkParentMenus(menus);
      const initialPages: GerenciadorPaginasItens[] = [];
      const finalPages = [
        ...organizeMenus(
          updated_menus,
          updated_menus.filter((item) => item.id_pagina_menu_pai === null),
          initialPages,
          0
        ),
      ];

      var updated_permissions = permissions ? [...permissions] : [];
      const uncheckedMenus = updated_menus.filter((menu) => !menu.checked);
      const checkedMenus = updated_menus.filter((menu) => menu.checked);
      uncheckedMenus.map((menu) => {
        const index = updated_permissions.findIndex(
          (p) => p.id_pagina_menu === menu.id_pagina_menu
        );
        if (index > -1) {
          updated_permissions.splice(index, 1);
        }
      });
      checkedMenus.map((menu) => {
        const perm = updated_permissions.find(
          (p) => p.id_pagina_menu === menu.id_pagina_menu
        );
        if (!perm) {
          updated_permissions.push({
            id_pagina_menu: menu.id_pagina_menu!,
            id_grupo_permissao: selectedGroup.id_grupo_permissao!,
          });
        }
      });

      setPermissions([...updated_permissions]);
      setPages(finalPages);
      setHasChanges(true);
    }
  }, [menus]);

  useEffect(() => {
    const elements = pages
      .sort((a, b) => new SortFunctions(a, b).sortMenus())
      .map((item) => {
        return renderItems(item, false);
      });
    setElementsMenus(elements);
  }, [pages]);

  const renderItems = (item: GerenciadorPaginasItens, children: boolean) => {
    var subelements: JSX.Element[] = [];
    if (item.children) {
      subelements = item.children.map((item) => {
        return renderItems(item, true);
      });
    }

    return (
      <MenuToolManagerItem
        key={`root_${item.pagina}`}
        item={item}
        subelements={subelements}
        children={children}
        onCheck={(item) => setMenus(onCheckMenus(item, menus))}
        onCollapse={(item) => {
          setMenus(onCollapseMenus(item, menus));
        }}
        onUpdate={() => {}}
        onUpdateDrag={() => {}}
        showChecked={true}
        showCollapse={true}
      />
    );
  };

  const onClose = () => {
    setShowModal(false);
    setMenus([]);
    setPages([]);
    setPermissions(undefined);
    setElementsMenus([]);
    setHasChanges(false);
    setCheckAll(false);
    setCollapseAll(false);
  };

  const onSave = async () => {
    if (selectedGroup && permissions) {
      onUpdatePermissions(selectedGroup.id_grupo_permissao!, permissions);
      onClose();
    }
  };

  return (
    <Modal
      show={showModal}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={onClose}
    >
      {selectedGroup && (
        <>
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Permissões para '{selectedGroup.nome}'
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Container className="d-flex vertical-center my-3">
              <Container className="width-full d-flex d-row justify-content-start">
                {collapseAll ? (
                  <div
                    className="d-flex vertical-center button strong-hover"
                    onClick={() => {
                      const { items, collapse } = collapseAllMenus(
                        menus,
                        collapseAll
                      );
                      setMenus(items);
                      setCollapseAll(collapse);
                    }}
                  >
                    <BiExpandVertical size={20} />
                    Expandir Todos
                  </div>
                ) : (
                  <div
                    className="d-flex vertical-center button strong-hover"
                    onClick={() => {
                      const { items, collapse } = collapseAllMenus(
                        menus,
                        collapseAll
                      );
                      setMenus(items);
                      setCollapseAll(collapse);
                    }}
                  >
                    <BiCollapseVertical size={20} />
                    Recolher Todos
                  </div>
                )}

                {checkAll ? (
                  <div
                    className="d-flex vertical-center button strong-hover mx-2"
                    onClick={() => {
                      const { items, check } = checkAllMenus(menus, checkAll);
                      setMenus(items);
                      setCheckAll(check);
                    }}
                  >
                    <BiCheckSquare size={20} />
                    Desmarcar Todos
                  </div>
                ) : (
                  <div
                    className="d-flex vertical-center button strong-hover mx-2"
                    onClick={() => {
                      const { items, check } = checkAllMenus(menus, checkAll);
                      setMenus(items);
                      setCheckAll(check);
                    }}
                  >
                    <BiCheckbox size={20} />
                    Marcar Todos
                  </div>
                )}
              </Container>
            </Container>

            {<ul>{elementsMenus}</ul>}
          </Modal.Body>

          <Modal.Footer>
            <div
              style={{ width: "100%" }}
              className="d-flex justify-content-between"
            >
              <Button variant="outline-danger" type="submit" onClick={onClose}>
                Fechar
              </Button>
              <Button
                disabled={!hasChanges}
                variant="outline-success"
                type="submit"
                onClick={onSave}
              >
                Salvar
              </Button>
            </div>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ModalPermissionsMenus;
